<ng-template #jumpToElement>
  <app-qa-question-index
    [questionnaire]="questionnaire"
    [mappings]="mappings"
    [pages]="pages"
    [pageViewEnabled]="pageViewEnabled"
    [currentPageIndex]="currentPageIndex"
    [locale]="locale"
    (jump)="jumpToQuestion($event)"
    (done)="jumpToQuestionPopover.close()"
  >
  </app-qa-question-index>
</ng-template>

<ng-template #filtergroupPicker>
  <div *ngIf="filtergroups?.length >= FILTERGROUP_SEARCH_THRESHOLD" class="qa-filtergroup-search">
    <i class="fa fa-search" aria-hidden="true"></i>
    <input type="search" [(ngModel)]="filtergroupQuery" [attr.aria-label]="_(KEYS.EDITOR.SEARCH_FILTER_GROUPS)"
           placeholder="{{_(KEYS.EDITOR.SEARCH_FILTER_GROUPS)}}">
  </div>
  <div class="qa-filtergroup-list" role="group" [attr.aria-label]="_(KEYS.EDITOR.FILTER_GROUPS)">
    <label *ngFor="let filtergroup of getFiltergroupOptions()" class="qa-filtergroup-option">
      <input type="checkbox" [checked]="hasFiltergroup(filtergroup.id)" (change)="toggleFiltergroup(filtergroup.id)">
      <span>{{filtergroup.name[locale]}}</span>
    </label>
    <p *ngIf="!filtergroups?.length" class="qa-filtergroup-empty">{{_(KEYS.EDITOR.NO_FILTER_GROUPS)}}</p>
    <p *ngIf="filtergroups?.length && !getFiltergroupOptions().length" class="qa-filtergroup-empty">
      {{_(KEYS.EDITOR.NO_MATCHING_FILTER_GROUPS)}}
    </p>
  </div>
</ng-template>

<!-- memorized unsaved changes: offered in the header instead of a blocking question (classic layout: above the header) -->
<ng-template #resumeHint>
  <div class="qa-resume-hint" *ngIf="pendingSession" role="status">
    <span class="qa-resume-text">
      <span class="qa-resume-dot" aria-hidden="true"></span>
      {{_(KEYS.EDITOR.RESUME_HINT)}}
      <strong *ngIf="pendingSession.title && (!editMode || pendingSession.id !== questionnaire?.id)">„{{pendingSession.title}}“</strong>
      <span class="qa-resume-time" *ngIf="pendingSession.savedAt">· {{pendingSession.savedAt | date:'dd.MM.yyyy, HH:mm'}}</span>
    </span>
    <div class="qa-toolbar-end">
      <button type="button" class="qa-tool" (click)="onResumeSession()">
        <i class="fa fa-undo" aria-hidden="true"></i><span>{{_(KEYS.EDITOR.RESUME_APPLY)}}</span>
      </button>
      <button type="button" class="qa-tool qa-tool-muted" (click)="onDiscardSession()">
        <i class="fa fa-times" aria-hidden="true"></i><span>{{_(KEYS.EDITOR.RESUME_DISCARD)}}</span>
      </button>
    </div>
  </div>
</ng-template>

<div class="qa-editor" #qaEditor [class.qa-classic-editor]="classicLayout">
  <app-qa-io-overlay
    [visible]="ioOverlayVisible"
    [questionnaire]="questionnaire"
    [pathHashMap]="pathHashMap"
    (onHide)="ioOverlayVisible = false"
    (onImportQuestionnaire)="questionnaire = $event"
  >
  </app-qa-io-overlay>

  <app-questionnaire-translation
    [visible]="translationOverlayVisible"
    [questionnaire]="questionnaire"
    (onHide)="translationOverlayVisible = false"
    (onUpdateQuestionnaire)="questionnaire = $event"
  >
  </app-questionnaire-translation>

  <app-qa-ai-assistant
    *ngIf="aiAssistantEnabled"
    [visible]="aiOverlayVisible"
    [questionnaire]="questionnaire"
    [pathHashMap]="pathHashMap"
    [locale]="locale"
    (onHide)="aiOverlayVisible = false"
    (onImportQuestionnaire)="questionnaire = $event"
  >
  </app-qa-ai-assistant>


  <ng-container *ngIf="classicLayout" [ngTemplateOutlet]="resumeHint"></ng-container>
  <div class="qa-meta" #qaMeta>
    <div class="qa-meta-document">
      <input
        class="qa-title"
        placeholder="{{_(KEYS.EDITOR.ENTER_TITLE)}}"
        [attr.aria-label]="_(KEYS.EDITOR.ENTER_TITLE)"
        [(ngModel)]="questionnaire.meta.title[locale]">

      <div class="qa-meta-info">
        <span class="qa-status-label" *ngIf="classicLayout">{{_(KEYS.EDITOR.STATUS)}}</span>
        <select
          class="qa-status-select"
          [class.qa-status-active]="questionnaire.status === 'active'"
          [attr.aria-label]="_(KEYS.EDITOR.STATUS)"
          [(ngModel)]="questionnaire.status"
        >
          <option value="draft">{{_(KEYS.EDITOR.DRAFT)}}</option>
          <option value="active">{{_(KEYS.EDITOR.APPROVED)}}</option>
        </select>
        <span class="qa-revision">
          {{_(KEYS.EDITOR.REV)}}
          {{questionnaire.revision <= 0 ? 1 : questionnaire.revision}}
        </span>
        <span class="qa-meta-dot" aria-hidden="true">·</span>
        <button
          type="button"
          class="qa-meta-link"
          [ngbPopover]="filtergroupPicker"
          placement="bottom-right"
          container="body"
          triggers="manual"
          [autoClose]="'outside'"
          popoverClass="qa-filtergroup-popover"
          #filtergroupPopover="ngbPopover"
          [attr.title]="getFiltergroupSummary()"
          (click)="toggleFiltergroupPicker(filtergroupPopover)"
        >
          <span class="qa-meta-link-label">{{_(KEYS.EDITOR.FILTER_GROUPS)}}:</span>
          <span class="qa-meta-link-value">{{getFiltergroupSummary() || _(KEYS.EDITOR.NO_FILTER_GROUPS)}}</span>
          <i class="fa fa-angle-down" aria-hidden="true"></i>
        </button>
      </div>

      <button
        class="btn btn-primary qa-save"
        [attr.disabled]="saveInProgress? true: null"
        (click)="onSaveQuestionnaire()"
      >
        <i class="fa fa-save" aria-hidden="true"></i>
        {{_(KEYS.EDITOR.SAVE)}}
      </button>
    </div>

    <ng-container *ngIf="!classicLayout" [ngTemplateOutlet]="resumeHint"></ng-container>

    <div class="qa-toolbar" role="toolbar">
      <!-- classic layout: show menu and fold buttons of the old editor -->
      <ng-container *ngIf="classicLayout">
        <div ngbDropdown class="qa-tool-wrap">
          <button type="button" class="qa-tool" ngbDropdownToggle>{{_(KEYS.EDITOR.SHOW)}}</button>
          <div ngbDropdownMenu>
            <button type="button" ngbDropdownItem (click)="tabs.onFoldQuestions()">{{_(KEYS.EDITOR.NO_CONTENT)}}</button>
            <button type="button" ngbDropdownItem (click)="tabs.onExpandQuestionContents()">{{_(KEYS.EDITOR.ALL_CONTENT)}}</button>
            <button type="button" ngbDropdownItem (click)="tabs.onExpandQuestionImages()">{{_(KEYS.EDITOR.ALL_IMAGES)}}</button>
            <button type="button" ngbDropdownItem (click)="tabs.onExpandQuestionVideos()">{{_(KEYS.EDITOR.ALL_VIDEOS)}}</button>
            <button type="button" ngbDropdownItem (click)="tabs.onExpandQuestionAudio()">{{_(KEYS.EDITOR.ALL_AUDIO)}}</button>
            <button type="button" ngbDropdownItem (click)="tabs.onExpandQuestionDescriptions()">{{_(KEYS.EDITOR.ALL_DESCRIPTIONS)}}</button>
            <button type="button" ngbDropdownItem (click)="tabs.onExpandQuestionConditions()">{{_(KEYS.EDITOR.ALL_DEPENDENCIES)}}</button>
            <button type="button" ngbDropdownItem (click)="tabs.onExpandQuestionVariables()">{{_(KEYS.EDITOR.ALL_VARIABLES)}}</button>
          </div>
        </div>
        <button type="button" class="qa-tool qa-tool-icon" (click)="tabs.onExpandQuestions()" ngbTooltip="{{_(KEYS.EDITOR.EXPAND_ELEMENTS)}}"
                [attr.aria-label]="_(KEYS.EDITOR.EXPAND_ELEMENTS)"><i class="fa fa-expand" aria-hidden="true"></i></button>
        <button type="button" class="qa-tool qa-tool-icon" (click)="tabs.onFoldQuestions()" ngbTooltip="{{_(KEYS.EDITOR.FOLD_ELEMENTS)}}"
                [attr.aria-label]="_(KEYS.EDITOR.FOLD_ELEMENTS)"><i class="fa fa-compress" aria-hidden="true"></i></button>
      </ng-container>
      <span ngbTooltip="{{_(KEYS.EDITOR.JUMP_TO_ELEMENT)}}" class="qa-tool-wrap">
        <button
          type="button"
          class="qa-tool"
          [ngbPopover]="jumpToElement"
          placement="bottom-left"
          popoverTitle="{{_(KEYS.EDITOR.JUMP_TO_ELEMENT)}}"
          triggers="manual" container="body"
          #jumpToQuestionPopover="ngbPopover"
          popoverClass="qa-jump-popover"
          (click)="onJumpToQuestion(jumpToQuestionPopover)"
        >
          <i class="fa fa-forward" aria-hidden="true"></i>
          <span>{{_(KEYS.EDITOR.JUMP)}}</span>
        </button>
      </span>
      <button
        type="button"
        class="qa-tool"
        [class.active]="pageViewEnabled"
        [attr.aria-pressed]="pageViewEnabled"
        [ngbTooltip]="classicLayout ? _(KEYS.EDITOR.PAGE_VIEW) : ''"
        (click)="togglePageView()"
      >
        <i class="fa fa-file-text-o" aria-hidden="true"></i>
        <span>{{_(KEYS.EDITOR.PAGE_VIEW)}}</span>
      </button>
      <app-qa-preview [questionnaire]="questionnaire"></app-qa-preview>

      <span class="qa-tool-separator" aria-hidden="true"></span>

      <button type="button" class="qa-tool" (click)="ioOverlayVisible = true" [ngbTooltip]="classicLayout ? _(KEYS.EDITOR.IMPORT_EXPORT) : ''">
        <i class="fa fa-retweet" aria-hidden="true"></i>
        <span>{{_(KEYS.EDITOR.IMPORT_EXPORT)}}</span>
      </button>
      <button *ngIf="canCloudTranslate" type="button" class="qa-tool" (click)="translationOverlayVisible = true"
              [ngbTooltip]="classicLayout ? _(KEYS.EDITOR.TRANSLATE) : ''">
        <i class="fa fa-language" aria-hidden="true"></i>
        <span>{{_(KEYS.EDITOR.TRANSLATE)}}</span>
      </button>
      <button *ngIf="aiAssistantEnabled" type="button" class="qa-tool" (click)="aiOverlayVisible = true"
              [ngbTooltip]="classicLayout ? _(KEYS.EDITOR.AI_ASSISTANT) : ''">
        <i class="fa fa-magic" aria-hidden="true"></i>
        <span>{{_(KEYS.EDITOR.AI_ASSISTANT)}}</span>
      </button>
      <button
        type="button"
        class="qa-tool qa-settings-toggle"
        [ngbTooltip]="classicLayout ? _(KEYS.EDITOR.QUESTIONNAIRE_SETTINGS) : ''"
        (click)="domHelper.toggleCls(qaMeta, 'expanded', [qaMetaTab])"
      >
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>{{_(KEYS.EDITOR.QUESTIONNAIRE_SETTINGS)}}</span>
        <i class="fa fa-toggle-expand" aria-hidden="true"></i>
      </button>

      <div class="qa-toolbar-end">
        <div class="language-icons">
          <span
            *ngFor="let lang of getLangCodes()"
            class="flag-icon flag-icon-{{flagIconCodeMap[lang]}}"
            [ngbTooltip]="translateLanguage(languages[lang])"
          ></span>
        </div>
        <span class="qa-bar-info">{{questionnaire.questions.length}} {{_(KEYS.EDITOR.ELEMENTS)}}</span>
        <button
          type="button"
          class="qa-tool qa-tool-icon"
          ngbTooltip="{{_(KEYS.EDITOR.FULL_SCREEN)}}"
          [attr.aria-label]="_(KEYS.EDITOR.FULL_SCREEN)"
          (click)="domHelper.toggleCls(qaEditor, 'maximized')"
        >
          <i class="fa fa-toggle-maximize" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>
  <div class="qa-meta-tab" #qaMetaTab>
    <div class="qa-settings">
      <ul ngbNav #nav="ngbNav" orientation="vertical" class="qa-settings-nav" (shown)="onMetaTabShown($event)">
        <li class="qa-settings-group">{{_(KEYS.EDITOR.SETTINGS_GENERAL)}}</li>
        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-align-left" aria-hidden="true"></i> {{_(KEYS.EDITOR.DESCRIPTION)}}</a>
          <ng-template ngbNavContent>
            <app-qa-lazy-froala
              class="qa-settings-description"
              [options]="defaultEditorOptions"
              [block]="true"
              [model]="questionnaire.meta.description[locale]"
              (modelChange)="questionnaire.meta.description[locale] = $event"
            ></app-qa-lazy-froala>
          </ng-template>
        </li>

        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-image" aria-hidden="true"></i> {{_(KEYS.EDITOR.IMAGE)}}</a>
          <ng-template ngbNavContent>
            <div class="qa-settings-image">
              <button type="button" class="qa-settings-image-preview" (click)="onOpenMediaCenterModal(questionnaire.meta.asset.image)">
                <img
                  *ngIf="questionnaire.meta.asset.image && questionnaire.meta.asset.image.filename[locale]; else qaEditorImgPlaceholder"
                  [src]="mediaCenter.getAssetUrl(questionnaire.meta.asset.image, locale)" alt="" />
                <ng-template #qaEditorImgPlaceholder>
                  <i class="fa fa-image" aria-hidden="true"></i>
                </ng-template>
              </button>
              <div class="qa-settings-image-actions">
                <button type="button" class="btn btn-primary" #qaEditorImageUpload (click)="onOpenMediaCenterModal()">
                  <i class="fa fa-plus" aria-hidden="true"></i> {{_(KEYS.EDITOR.ADD_IMAGE)}}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-danger"
                  (click)="onDeleteImage()"
                  [attr.disabled]="!(questionnaire.meta.asset.image && questionnaire.meta.asset.image.filename[locale])? true: null">
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </ng-template>
        </li>

        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-pencil-square-o" aria-hidden="true"></i> {{_(KEYS.EDITOR.FILL_OPTIONS)}}</a>
          <ng-template ngbNavContent>
            <div class="qa-switch-list">
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.FORCE_MANDATORY)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.fill.forceMandatory"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.SKIP_LIST)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.fill.skipList"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.SKIP_SUBMIT_CONFIRMATION)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.fill.skipSubmitConfirmation"
            >
          </label>
            </div>
          </ng-template>
        </li>

        <li class="qa-settings-group">{{_(KEYS.EDITOR.SETTINGS_EVALUATION)}}</li>
        <li [ngbNavItem]="'scoring'">
          <a ngbNavLink><i class="fa fa-sort-numeric-asc" aria-hidden="true"></i> {{_(KEYS.EDITOR.SCORING)}}</a>
          <ng-template ngbNavContent>
            <app-qa-scoring-variables [questionnaire]="questionnaire"></app-qa-scoring-variables>
          </ng-template>
        </li>

        <li ngbNavItem>
          <a ngbNavLink [class.formula-tab-broken]="hasBrokenFormulas()"><i class="fa fa-calculator" aria-hidden="true"></i> {{_(KEYS.EDITOR.FORMULA_VARIABLES)}}</a>
          <ng-template ngbNavContent>
            <app-qa-formula-variables [questionnaire]="questionnaire" [locale]="locale"></app-qa-formula-variables>
          </ng-template>
        </li>

        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-external-link" aria-hidden="true"></i> {{_(KEYS.EDITOR.EXTERN_VARIABLES)}}</a>
          <ng-template ngbNavContent>
            <app-qa-extern-variables [questionnaire]="questionnaire"></app-qa-extern-variables>
          </ng-template>
        </li>

        <li class="qa-settings-group">{{_(KEYS.EDITOR.SETTINGS_OUTPUT)}}</li>
        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-file-pdf-o" aria-hidden="true"></i> {{_(KEYS.EDITOR.PDF)}}</a>
          <ng-template ngbNavContent>
            <div class="qa-switch-list">
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.PDF_PLAIN_CARDS)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.pdf.plainCards"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.PDF_COMPACT_SPACING)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.pdf.compactSpacing"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.SKIP_UNSIGNED_PDF)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.pdf.skipUnsigned"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.GENERATE_INTERNAL_PDF)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.pdf.generateInternal"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.PDF_FONT_SCALE)}}</span>
                <select
                  class="form-control qa-pdf-font-scale"
                  [ngModelOptions]="{standalone: true}"
                  [(ngModel)]="questionnaire.meta.options.pdf.fontScale"
                >
                  <option [ngValue]="undefined">100% ({{_(KEYS.EDITOR.PDF_FONT_SCALE_DEFAULT)}})</option>
                  <option [ngValue]="0.9">90%</option>
                  <option [ngValue]="0.8">80%</option>
                  <option [ngValue]="0.7">70%</option>
                  <option [ngValue]="0.6">60%</option>
                </select>
              </label>
            </div>
          </ng-template>
        </li>

        <li ngbNavItem *ngIf="feature.pdf.forms">
          <a ngbNavLink><i class="fa fa-files-o" aria-hidden="true"></i> {{_(KEYS.EDITOR.PDF_FORMS)}}</a>
          <ng-template ngbNavContent>
            <div class="qa-settings-hint">
              <span class="qa-settings-hint-icon"><i class="fa fa-file-text-o" aria-hidden="true"></i></span>
              <div class="qa-settings-hint-body">
                <strong>{{_(KEYS.EDITOR.PDF_FORMS_INTRO)}}</strong>
                <p>{{_(KEYS.EDITOR.PDF_FORMS_HELP)}}</p>
                <button type="button" class="qa-settings-hint-link" (click)="nav.select('pdfMapping')">
                  {{_(KEYS.EDITOR.PDF_FORMS_OPEN_MAPPING)}} <i class="fa fa-long-arrow-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <form class="pdf-form-opts">
              <app-pdf-form-select
                [selected]="questionnaire.meta.options.pdf.forms"
                (selectedChange)="questionnaire.meta.options.pdf.forms = $event">
              </app-pdf-form-select>
            </form>
          </ng-template>
        </li>

        <li [ngbNavItem]="'pdfMapping'">
          <a ngbNavLink><i class="fa fa-code" aria-hidden="true"></i> PDF Mapping</a>
          <ng-template ngbNavContent>
            <form class="pdf-form-opts">
              <app-qa-map-variables></app-qa-map-variables>
            </form>
          </ng-template>
        </li>

        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-exchange" aria-hidden="true"></i> {{_(KEYS.EDITOR.GDT)}}</a>
          <ng-template ngbNavContent>
            <div class="qa-switch-list">
              <div class="qa-switch-row">
                <span class="qa-switch-label">
                  {{_(KEYS.EDITOR.ADD_QUESTIONNAIRE_TITLE_TO_GDT)}}
                  <button
                    type="button"
                    class="qa-switch-code"
                    (click)="onGdtMetaTitleVariablesModal()"
                    [attr.disabled]="!questionnaire.meta.options.gdt.addQuestionnaireTitle ? true : null">
                    <i class="fa fa-code" aria-hidden="true"></i>
                  </button>
                </span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
                  [(ngModel)]="questionnaire.meta.options.gdt.addQuestionnaireTitle"
                >
              </div>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.ADD_SCORING_TO_GDT)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.gdt.addScoring"
            (change)="addDefaultGdtScoringVariables()"
            >
          </label>
              <div *ngIf="questionnaire.meta.options.gdt.addScoring && hasScoring()" class="qa-switch-sublist">
                <div *ngFor="let scoringVar of getScoringVariables()" class="qa-switch-subrow">
                  <span>{{scoringVar}}</span>
                  <button type="button" class="qa-switch-code" (click)="onGdtMetaScoringVariablesModal(scoringVar)">
                    <i class="fa fa-code" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.ADD_FORMULA_RESULTS_TO_GDT)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.gdt.addFormulaResults"
            (change)="addDefaultGdtFormulaVariables()"
            >
          </label>
              <div *ngIf="questionnaire.meta.options.gdt.addFormulaResults && hasFormulaVariables()" class="qa-switch-sublist">
                <div *ngFor="let formulaVar of getFormulaVariables()" class="qa-switch-subrow">
                  <span>{{formulaVar}}</span>
                  <button type="button" class="qa-switch-code" (click)="onGdtMetaFormulaVariablesModal(formulaVar)">
                    <i class="fa fa-code" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          </ng-template>
        </li>

        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-envelope-o" aria-hidden="true"></i> {{_(KEYS.EDITOR.MAIL)}}</a>
          <ng-template ngbNavContent>
            <h4 class="qa-settings-subtitle"><i class="fa fa-user" aria-hidden="true"></i> {{_(KEYS.EDITOR.USER)}}</h4>
            <div class="qa-switch-list">
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.SEND_INTERNAL_PDF)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.mail.user.internalPdf"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.SEND_FILLED_PDF)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.mail.user.filledPdf"
            >
          </label>
            </div>
            <h4 class="qa-settings-subtitle"><i class="fa fa-pencil-square-o" aria-hidden="true"></i> {{_(KEYS.EDITOR.FILLER)}}</h4>
            <div class="qa-switch-list">
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.SEND_INTERNAL_PDF)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.mail.filler.internalPdf"
            >
          </label>
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.SEND_FILLED_PDF)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.mail.filler.filledPdf"
            >
          </label>
            </div>
          </ng-template>
        </li>

        <li ngbNavItem>
          <a ngbNavLink><i class="fa fa-table" aria-hidden="true"></i> {{_(KEYS.EDITOR.EXCEL)}}</a>
          <ng-template ngbNavContent>
            <div class="qa-switch-list">
              <label class="qa-switch-row">
                <span class="qa-switch-label">{{_(KEYS.EDITOR.OMIT_PATIENT_DATA_IN_TABLE_EXPORT)}}</span>
                <input
                  type="checkbox"
                  class="qa-switch"
                  [ngModelOptions]="{standalone: true}"
              [(ngModel)]="questionnaire.meta.options.excel.omitPatientData"
            >
          </label>
            </div>
          </ng-template>
        </li>
      </ul>
      <div class="qa-settings-content" [ngbNavOutlet]="nav"></div>
    </div>
  </div>
  <div class="qa-editor-workspace" #workspace [class.qa-app-layout]="appLayout" [class.qa-classic-layout]="classicLayout" [class.qa-palette-collapsed]="paletteCollapsed">
    <aside class="qa-palette-column">
      <button type="button" class="qa-palette-toggle" (click)="paletteCollapsed = !paletteCollapsed"
              [attr.aria-expanded]="!paletteCollapsed" aria-controls="qa-element-palette"
              [attr.aria-label]="_(KEYS.EDITOR.ADD_ELEMENT)" [attr.title]="_(KEYS.EDITOR.ADD_ELEMENT)">
        <span class="qa-palette-label">{{_(KEYS.EDITOR.ADD_ELEMENT)}}</span>
        <i class="fa" [ngClass]="paletteCollapsed ? 'fa-angle-right' : 'fa-angle-left'" aria-hidden="true"></i>
      </button>
      <div id="qa-element-palette" [hidden]="paletteCollapsed">
        <app-qa-add-question #elementPalette [palette]="true"
          [pageViewEnabled]="pageViewEnabled"
          [pages]="pages"
          [currentPageIndex]="currentPageIndex"
        ></app-qa-add-question>
      </div>
    </aside>
    <div class="qa-questions" #questionLayout
         [class.qa-split-resized]="inspectorWidth !== null" [class.qa-split-resizing]="isResizingInspector"
         [style.--qa-inspector-width]="inspectorWidth === null ? null : inspectorWidth + '%'">
    <div class="qa-document-column">
      <app-qa-add-question *ngIf="classicLayout" class="qa-classic-add" [pageViewEnabled]="pageViewEnabled" [pages]="pages"
                           [currentPageIndex]="currentPageIndex"></app-qa-add-question>
      <div *ngIf="!questionnaire?.questions?.length" class="qa-document-empty qa-palette-drop-target" [class.qa-palette-drag-active]="paletteDragging">
        <i class="fa fa-file-text-o" aria-hidden="true"></i>
        <h3>{{_(KEYS.EDITOR.QUESTIONNAIRE_EDITOR)}}</h3>
        <p>{{_(KEYS.EDITOR.ADD_ELEMENT)}}</p>
      </div>


    <ng-container *ngIf="questionnaire">
      <div class="qa-page-navigation" #pageNavigation *ngIf="pageViewEnabled && pages.length > 0">
        <div class="qa-page-selector">
          <button type="button" class="btn btn-outline-secondary qa-page-step"
                  [disabled]="!canGoToPage(-1)" (click)="goToPage(-1)"
                  [attr.aria-label]="_(KEYS.EDITOR.PREVIOUS_PAGE)" ngbTooltip="{{_(KEYS.EDITOR.PREVIOUS_PAGE)}}">
            <i class="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <app-select2 [hideButton]="true" [hideLabel]="true" [options]="pageSelectOptions"
                       [(selected)]="currentPageIndex" (selectedChange)="onPageChange($event)">
            <option *ngFor="let page of pages; let i = index" [value]="i">
              {{_(KEYS.EDITOR.PAGE)}} {{i + 1}}{{page.title ? ': ' + page.title : ''}}
            </option>
          </app-select2>
          <button type="button" class="btn btn-outline-secondary qa-page-step"
                  [disabled]="!canGoToPage(1)" (click)="goToPage(1)"
                  [attr.aria-label]="_(KEYS.EDITOR.NEXT_PAGE)" ngbTooltip="{{_(KEYS.EDITOR.NEXT_PAGE)}}">
            <i class="fa fa-chevron-right" aria-hidden="true"></i>
          </button>
          <span class="qa-page-count" aria-live="polite" [attr.aria-label]="_(KEYS.EDITOR.PAGE) + ' ' + (currentPageIndex + 1) + ' / ' + pages.length">
            {{currentPageIndex + 1}} / {{pages.length}}
          </span>
        </div>
        <div class="qa-page-actions">
          <div ngbDropdown placement="bottom-right" class="qa-page-menu">
            <button type="button" class="btn btn-outline-secondary qa-page-step" ngbDropdownToggle
                    [attr.aria-label]="_(KEYS.EDITOR.PAGE) + ': ' + _(KEYS.EDITOR.OPTIONS)"
                    ngbTooltip="{{_(KEYS.EDITOR.OPTIONS)}}">
              <i class="fa fa-ellipsis-v" aria-hidden="true"></i>
            </button>
            <div ngbDropdownMenu>
              <button type="button" ngbDropdownItem class="qa-copy-page-btn" (click)="copyCurrentPage()">
                <i class="fa fa-copy" aria-hidden="true"></i> {{_(KEYS.EDITOR.COPY_PAGE)}}
              </button>
              <button type="button" ngbDropdownItem class="qa-delete-page-btn" (click)="deleteCurrentPage()">
                <i class="fa fa-trash" aria-hidden="true"></i> {{_(KEYS.EDITOR.DELETE_PAGE)}}
              </button>
            </div>
          </div>
          <button type="button" class="btn btn-primary qa-add-page-btn qa-page-step" (click)="addNewPage()"
                  [attr.aria-label]="_(KEYS.EDITOR.ADD_PAGE)" ngbTooltip="{{_(KEYS.EDITOR.ADD_PAGE)}}">
            <i class="fa fa-plus" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div class="qa-document-scroll" [style.--qa-drop-label]="dropLabel">
        <div class="qa-page-title" *ngIf="currentPageBreak as pageBreak">
          <input class="form-control" [ngModel]="pageBreak.title?.[locale]" (ngModelChange)="setPageTitle($event)"
                 placeholder="{{_(KEYS.EDITOR.ENTER_TITLE)}}" [attr.aria-label]="_(KEYS.EDITOR.PAGE) + ' ' + (currentPageIndex + 1) + ': ' + _(KEYS.EDITOR.TITLE)">
        </div>
        <div class="qa-insert-between" *ngIf="pageViewEnabled && !classicLayout">
          <ng-template #addPageStartElement>
            <app-qa-add-question [position]="getPageStartIndex() - 1" [hideLabel]="true"
                                 (done)="pageStartPopover.close()"></app-qa-add-question>
          </ng-template>
          <button type="button" class="qa-insert-button" [ngbPopover]="addPageStartElement"
                  placement="bottom" container="body" triggers="manual" [autoClose]="false"
                  popoverTitle="{{_(KEYS.EDITOR.ADD_ELEMENT)}}" #pageStartPopover="ngbPopover"
                  (click)="openPageStartElementPicker(pageStartPopover, $event)">
            <i class="fa fa-plus" aria-hidden="true"></i> {{_(KEYS.EDITOR.INSERT_ELEMENT_HERE)}}
          </button>
        </div>

        <!-- flat view: iterate directly over questions for better performance -->
        <div
          class="elements qa-palette-drop-target"
          [dragula]='"elements-bag"'
          [dragulaModel]="questionnaire.questions"
          *ngIf="!pageViewEnabled"
        >
          <ng-container *ngFor="let question of questionnaire.questions; let i = index; trackBy: trackByQuestion">
            <ng-container *ngIf="mappings[question.format]">
              <ng-container
                *ngComponentOutlet="mappings[question.format].main;
                ndcDynamicInputs: {position: i, feature: feature, question: question, pageViewEnabled: false, isFirstOnPage: false, isLastOnPage: false, mapping: mappings[question.extra ? question.format + '#' + question.extra : question.format]};">
              </ng-container>
            </ng-container>
          </ng-container>
        </div>

        <!-- page view: iterate over filtered visible questions -->
        <div
          class="elements qa-palette-drop-target"
          [dragula]='"elements-bag"'
          [dragulaModel]="visibleQuestions"
          *ngIf="pageViewEnabled"
        >
          <ng-container *ngFor="let item of visibleQuestions; trackBy: trackByQuestionId">
            <ng-container *ngIf="mappings[item.question.format]">
              <ng-container
                *ngComponentOutlet="mappings[item.question.format].main;
                ndcDynamicInputs: {position: item.realIndex, feature: feature, question: item.question, pageViewEnabled: true, isFirstOnPage: item.isFirstOnPage, isLastOnPage: item.isLastOnPage, pages: pages, currentPageIndex: currentPageIndex, mapping: mappings[item.question.extra ? item.question.format + '#' + item.question.extra : item.question.format]};">
              </ng-container>
            </ng-container>
          </ng-container>
        </div>
      </div>

      <div class="qa-page-nav-buttons" *ngIf="pageViewEnabled && pages.length > 0">
        <div class="qa-page-pager">
          <button type="button" class="btn btn-outline-secondary qa-page-step"
                  [disabled]="!canGoToPage(-1)" (click)="goToPage(-1); scrollToPageStart()"
                  [attr.aria-label]="_(KEYS.EDITOR.PREVIOUS_PAGE)" ngbTooltip="{{_(KEYS.EDITOR.PREVIOUS_PAGE)}}">
            <i class="fa fa-chevron-left" aria-hidden="true"></i>
          </button>
          <span class="qa-page-info">{{_(KEYS.EDITOR.PAGE)}} {{currentPageIndex + 1}} / {{pages.length}}</span>
          <button type="button" class="btn btn-outline-secondary qa-page-step"
                  [disabled]="!canGoToPage(1)" (click)="goToPage(1); scrollToPageStart()"
                  [attr.aria-label]="_(KEYS.EDITOR.NEXT_PAGE)" ngbTooltip="{{_(KEYS.EDITOR.NEXT_PAGE)}}">
            <i class="fa fa-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
        <button type="button" class="btn btn-primary qa-add-page-btn qa-page-step" (click)="addNewPage(); scrollToPageStart()"
                  [attr.aria-label]="_(KEYS.EDITOR.ADD_PAGE)" ngbTooltip="{{_(KEYS.EDITOR.ADD_PAGE)}}">
          <i class="fa fa-plus" aria-hidden="true"></i>
        </button>
      </div>
    </ng-container>

    <div class="qa-buttons mt-3">
      <button
        class="btn btn-primary qa-upper"
        [attr.disabled]="saveInProgress? true: null"
        (click)="onSaveQuestionnaire()"
      >
        <i class="fa fa-save"></i>
        {{_(KEYS.EDITOR.SAVE)}}
      </button>
    </div>
    </div>
    <div class="qa-inspector-divider" role="separator" tabindex="0" aria-orientation="vertical"
         aria-controls="qa-question-inspector" aria-valuemin="0" aria-valuemax="100"
         [attr.aria-valuenow]="inspectorWidthValue" [attr.aria-label]="_(KEYS.EDITOR.RESIZE_PROPERTIES)"
         [attr.title]="_(KEYS.EDITOR.RESIZE_PROPERTIES_HINT)"
         (pointerdown)="startInspectorResize($event, questionLayout)"
         (pointerup)="endInspectorResize($event)" (pointercancel)="endInspectorResize($event)"
         (lostpointercapture)="endInspectorResize($event)"
         (focus)="updateInspectorWidthValue(questionLayout)"
         (keydown)="resizeInspectorWithKeyboard($event, questionLayout)"
         (dblclick)="resetInspectorWidth()">
      <span aria-hidden="true"></span>
    </div>
    <aside id="qa-question-inspector" class="qa-inspector" [attr.aria-label]="_(KEYS.EDITOR.PROPERTIES)">
      <div class="qa-inspector-header"><i class="fa fa-sliders" aria-hidden="true"></i><h3>{{_(KEYS.EDITOR.PROPERTIES)}}</h3></div>
      <div class="qa-inspector-scroll">
        <ng-container *ngIf="!classicLayout && (inspector.selection | async) as selected; else noSelection">
          <ng-container *ngTemplateOutlet="selected.template"></ng-container>
        </ng-container>
        <ng-template #noSelection>
          <div class="qa-inspector-empty"><i class="fa fa-hand-pointer-o" aria-hidden="true"></i><p>{{_(KEYS.EDITOR.SELECT_QUESTION_HINT)}}</p></div>
        </ng-template>
      </div>
    </aside>
    </div>
  </div>
</div>
